/**
 * Dry dock ve Pre-Docking Inspection metinleri (ADR 0129 Faz 1; denetim ekranı Faz 2, ADR 0129 Ek). Ortağın Excel'indeki adlar
 * İngilizcede aynen (Area, System, Checkpoint, Inspection Scope, Method, Timing, Acceptance
 * Criteria). Hata anahtarları sunucunun kodlarıyla eşleşir.
 */

export const drydockEn = {
  close: 'Close',
  cancel: 'Cancel',
  save: 'Save',
  library: {
    title: 'Pre-Docking Inspection Library',
    description:
      'Company master library of pre-docking inspection checkpoints: areas, systems and checkpoints with inspection scope, method, timing and acceptance criteria. A checkpoint ID never changes and is never reused. Publishing creates an immutable version; inspections use the version they were opened with.',
    shipDescription:
      'The company pre-docking inspection library, received from the office. Read-only on board; the office edits and publishes it.',
    error: 'The library could not be loaded.',
    tree: 'Areas and systems',
    checkpoints: 'Checkpoints',
    checkpointList: 'Checkpoint list',
    allAreas: 'All areas',
    versions: 'Published versions',
    unpublished:
      'The library has changes that are not published yet. Inspections opened now use the last published version.',
    noVersion: 'No version published yet. An inspection can be opened after the first publication.',
    draft:
      'The Felix default library is loaded as a draft. Review the areas, systems and checkpoints, then publish the first version; until then no pre-docking inspection can be opened.',
    draftNoPublish:
      'The Felix default library is loaded as a draft. A Superintendent reviews and publishes the first version; until then no pre-docking inspection can be opened.',
    disclaimer:
      'Draft guidance — not a class-approved checklist; vessel SMS, class, flag and maker instructions take precedence.',
  },
  stats: {
    areas: 'Areas',
    systems: 'Systems',
    checkpoints: 'Checkpoints',
    version: 'Published version',
  },
  columns: {
    code: 'ID',
    checkpoint: 'Checkpoint',
    system: 'System',
    method: 'Method',
    timing: 'Timing',
    status: 'Status',
    version: 'Version',
    label: 'Label',
    note: 'Note',
    publishedAt: 'Published',
    publishedBy: 'By',
  },
  fields: {
    code: 'ID',
    level: 'Level',
    parentCode: 'Parent',
    name: 'Name',
    sortOrder: 'Order',
    active: 'Active',
    scope: 'Inspection Scope',
    method: 'Method',
    timing: 'Timing',
    acceptance: 'Acceptance Criteria',
    note: 'Publication note',
  },
  levels: { area: 'Area', system: 'System', checkpoint: 'Checkpoint' },
  status: { all: 'All statuses', active: 'Active', inactive: 'Inactive' },
  search: 'Search by ID, checkpoint or scope',
  showing: '{{from}}–{{to}} of {{total}}',
  empty: {
    library: 'The library is empty. Load the Felix default library or add an area.',
    filtered: 'No checkpoint matches the filter.',
    versions: 'No published version.',
  },
  actions: {
    newArea: 'New Area',
    newSystem: 'New System',
    newCheckpoint: 'New Checkpoint',
    edit: 'Edit',
    publish: 'Publish Version',
    loadDefaults: 'Load Felix Default Library',
    reviewPublish: 'Review & Publish',
  },
  node: {
    titleNew: 'New {{level}}',
    titleEdit: 'Edit {{code}}',
    autoCode: 'The ID is given automatically: the next free number under {{parent}}.',
    fixedCode:
      'The ID, level and parent never change. Make the entry inactive instead of deleting it.',
  },
  publish: {
    title: 'Publish library version',
    body: 'The active library becomes version {{version}}: an immutable copy that new inspections will use. Inspections already open keep their version.',
    noteHint: 'Required from the second version: what changed and why.',
    unchanged: 'The library is the same as version {{version}}; no new version was created.',
    done: 'Version {{version}} published.',
  },
  defaults: {
    done: '{{version}}: {{areas}} areas, {{systems}} systems and {{checkpoints}} checkpoints added.',
    published: 'Version {{version}} published.',
    draft: 'Added as unpublished changes; a Superintendent publishes the version.',
    none: 'Nothing was missing.',
  },
  detail: {
    title: 'Checkpoint',
    path: '{{area}} › {{system}}',
  },
  projects: {
    title: 'Dry Docking Projects',
    description:
      'Dry docking and afloat repair projects of the vessel. A pre-docking inspection is opened from the published company library; its version is frozen for the inspection.',
    officeDescription:
      'Dry docking projects of the fleet, recorded on board. Read-only in the office.',
    error: 'The projects could not be loaded.',
    list: 'Project list',
    empty: 'No dry docking project yet.',
    new: 'New Project',
    titleNew: 'New dry docking project',
    titleEdit: 'Edit {{no}}',
    cancelTitle: 'Cancel {{no}}',
    cancelBody:
      'The project and its open inspection are cancelled with the reason. This cannot be undone; the record is kept.',
    reason: 'Reason',
    cancelProject: 'Cancel Project',
    openInspection: 'Open Pre-Docking Inspection',
    inspections: 'Pre-Docking Inspections',
    noInspection: 'No inspection opened.',
    libraryVersion: 'Library v{{version}} · {{count}} checkpoints',
    libraryNow: 'The company library is now at v{{version}}.',
    vessel: 'Vessel',
    allVessels: 'All vessels',
    noLibrary:
      'No inspection library version is published yet, so a pre-docking inspection cannot be opened. A Superintendent reviews and publishes the library in Dry Docking ▸ Inspection Library.',
    goInspection: 'Open in Pre-Docking Inspection',
  },
  projectColumns: {
    no: 'Project No',
    vessel: 'Vessel',
    title: 'Title',
    kind: 'Type',
    yard: 'Shipyard',
    planned: 'Planned',
    state: 'Status',
    inspection: 'Inspection',
  },
  projectFields: {
    title: 'Project title',
    kind: 'Type',
    yardName: 'Shipyard',
    yardLocation: 'Location',
    plannedStart: 'Planned start',
    plannedEnd: 'Planned end',
    notes: 'Notes',
  },
  kinds: { dry_docking: 'Dry Docking', afloat_repair: 'Afloat Repair' },
  states: {
    planning: 'Planning',
    in_yard: 'In Yard',
    completed: 'Completed',
    cancelled: 'Cancelled',
  },
  inspectionStates: { open: 'Open', closed: 'Closed', cancelled: 'Cancelled' },
  inspection: {
    title: 'Inspection {{no}}',
    frozen:
      'Opened on {{date}} with library version {{version}}. Results are recorded in Dry Docking ▸ Pre-Docking Inspection.',
  },
  work: {
    title: 'Pre-Docking Inspection',
    description:
      'Record the pre-docking inspection checkpoint by checkpoint: result, access, inspector, measurements and evidence. Only checkpoints are inspected. A saved result is not changed; a correction is a new revision with a reason. The Master or Chief Engineer reviews results. Recording a result does not open a finding, job or defect.',
    officeDescription:
      'Pre-docking inspections of the fleet, recorded on board. Read-only in the office.',
    error: 'The inspection could not be loaded.',
    vessel: 'Vessel',
    allVessels: 'All vessels',
    inspection: 'Inspection',
    noInspection: 'No inspection',
    empty: 'No pre-docking inspection yet. Open one from a dry docking project.',
    noLibrary:
      'No inspection library version is published yet, so an inspection cannot be opened. A Superintendent reviews and publishes the library in Dry Docking ▸ Inspection Library.',
    closed: 'This inspection is not open: results are read-only.',
    progress: 'Inspection progress {{percent}}%',
    progressText: '{{done}} of {{applicable}} applicable checkpoints inspected ({{percent}}%).',
    filter: 'Show',
    filters: {
      all: 'All',
      remaining: 'Remaining',
      adverse: 'Adverse',
      after_docking: 'After Docking',
    },
    stats: {
      applicable: 'Applicable',
      done: 'Inspected',
      remaining: 'Remaining',
      adverse: 'Adverse',
      deferred: 'After Docking',
      notApplicable: 'Not applicable',
    },
    columns: {
      code: 'ID',
      checkpoint: 'Checkpoint',
      system: 'System',
      status: 'Status',
      inspected: 'Inspected (LT)',
      inspector: 'Inspector',
      evidence: 'Evidence',
      pages: 'Checkpoint pages',
    },
    status: {
      satisfactory: 'Satisfactory',
      attention_required: 'Attention Required',
      unsatisfactory: 'Unsatisfactory',
      not_applicable: 'Not Applicable',
      deferred: 'Deferred',
      not_inspected: 'Not Inspected',
      vessel_na: 'N/A for vessel',
    },
    access: {
      accessible: 'Accessible',
      inaccessible: 'Inaccessible',
      after_docking: 'After docking',
    },
    applicability: {
      applicable: 'Applicable',
      not_applicable: 'Not applicable to vessel',
      review: 'Applicability to review',
    },
    verdict: { pass: 'Pass', fail: 'Fail', none: 'No limit' },
    result: 'Result',
    noResult: 'Not inspected yet.',
    vesselNa: 'Not applicable to this vessel: excluded from the count and takes no result.',
    history: 'Revision history',
    revision: 'Rev. {{no}}',
    revisionBy: 'Revision {{no}} · recorded by {{name}} · {{date}}',
    reviewed: 'Reviewed by {{name}} · {{date}}',
    notReviewed: 'Not reviewed yet.',
    recordTitle: 'Record result · {{code}}',
    correctTitle: 'Correct result · {{code}}',
    correctHint:
      'The saved result is not changed: the correction is kept as a new revision with your reason, and evidence is added to the latest revision.',
    applicabilityTitle: 'Applicability to vessel · {{code}}',
    applicabilityHint:
      'The vessel profile applies to every inspection of this vessel. A checkpoint not applicable to the vessel is excluded from the count and takes no result.',
    reviewTitle: 'Review result · {{code}}',
    reviewHint:
      'The Master or Chief Engineer reviews the result; nobody reviews a result they inspected or recorded.',
    actions: {
      record: 'Record Result',
      correct: 'Correct Result',
      review: 'Review',
      applicability: 'Set Applicability',
    },
  },
  resultFields: {
    result: 'Result',
    access: 'Access',
    inspectedAt: 'Inspected at',
    inspector: 'Inspector',
    inspectorId: 'Inspector',
    equipment: 'Equipment',
    equipmentId: 'Equipment',
    noEquipment: 'No equipment',
    notes: 'Condition notes',
    notesRequired: 'Required for Attention Required and Unsatisfactory.',
    reasonRequired: 'Write the reason (required for Not Applicable and Deferred).',
    measurements: 'Measurements',
    measurementNo: 'Measurement {{no}}',
    noMeasurements: 'No measurement.',
    addMeasurement: 'Add Measurement',
    removeMeasurement: 'Remove measurement',
    label: 'Measurement',
    value: 'Value',
    unit: 'Unit',
    lower: 'Lower limit',
    upper: 'Upper limit',
    limits: 'Limits',
    verdict: 'Verdict',
    reference: 'Acceptance reference',
    correctionReason: 'Reason for correction',
    applicability: 'Applicability',
    applicabilityReason: 'Reason',
    reason: 'Reason',
    reviewNote: 'Review note',
    evidence: 'Evidence',
    noEvidence: 'No evidence attached.',
    addEvidence: 'Add Evidence',
    checkpointCode: 'Checkpoint',
  },
  errors: {
    INVALID_PDI_NODE: 'Check the fields: {{fields}}',
    INVALID_DRYDOCK_PROJECT: 'Check the fields: {{fields}}',
    DUPLICATE_PDI_CODE: 'This ID already exists (IDs are never reused).',
    PDI_CODE_FIXED: 'The ID, level and parent of an entry cannot change.',
    PDI_PARENT_MISSING: 'The parent entry does not exist.',
    PDI_PARENT_INACTIVE: 'The parent entry is inactive.',
    PDI_CODE_EXHAUSTED: 'No free ID is left under this parent.',
    PDI_LIBRARY_EMPTY: 'The library has no active checkpoint to publish.',
    PDI_NOTE_REQUIRED: 'Write a publication note.',
    PDI_SNAPSHOT_MISMATCH: 'The library changed meanwhile; reload and publish again.',
    PDI_LIBRARY_NOT_PUBLISHED:
      'No library version is published yet. A Superintendent reviews and publishes the library first.',
    INVALID_PDI_RESULT: 'Check the fields: {{fields}}',
    INVALID_PDI_APPLICABILITY: 'Check the fields: {{fields}}',
    PDI_NOT_CHECKPOINT: 'Only a checkpoint can be inspected (not an area or system).',
    PDI_CHECKPOINT_UNKNOWN: 'This checkpoint is not in the library.',
    PDI_REVISION_SEQUENCE: 'The result changed meanwhile; reload and correct the latest revision.',
    PDI_RESULT_SUPERSEDED: 'A newer revision exists; work on the latest revision.',
    PDI_ALREADY_REVIEWED: 'This result is already reviewed.',
    PDI_NOT_APPLICABLE: 'The checkpoint is not applicable to the vessel; it takes no result.',
    PDI_INSPECTION_CLOSED: 'The inspection is not open.',
    PDI_INSPECTOR_NOT_ON_BOARD: 'The inspector was not on board on that day.',
    SELF_APPROVAL_FORBIDDEN:
      'You cannot review a record you inspected or raised; the company setting does not allow self-review.',
    VESSEL_MISMATCH: 'The equipment belongs to another vessel.',
    NOT_FOUND: 'The record was not found.',
    PDI_INSPECTION_OPEN: 'This project already has an open inspection.',
    DRYDOCK_PROJECT_CANCELLED: 'The project is cancelled.',
    FORBIDDEN: 'You do not have permission for this action.',
    ROW_VERSION_CONFLICT: 'The record changed meanwhile; reload and try again.',
    INVALID_PDI_FINDING: 'Check the fields: {{fields}}',
    INVALID_DRYDOCK_JOB: 'Check the fields: {{fields}}',
    INVALID_DRYDOCK_LINK: 'Check the link: {{fields}}',
    INVALID_DRYDOCK_DECISION: 'Check the fields: {{fields}}',
    DRYDOCK_RECORD_FIXED:
      'The number, project, inspection and checkpoint of a record cannot change.',
    DRYDOCK_RECORD_CANCELLED: 'The record is cancelled.',
    DUPLICATE_LINK: 'This link already exists.',
    DRYDOCK_REVIEW_SEQUENCE: 'The record changed meanwhile; reload and try again.',
    DRYDOCK_ALREADY_REVIEWED: 'This content is already reviewed or awaiting the office.',
    DRYDOCK_JOB_UNCHANGED: 'Change the job before submitting it again.',
    DRYDOCK_JOB_NO_ORIGIN: 'A job needs a finding or checkpoint as its origin.',
    DRYDOCK_DEFECT_EXISTS:
      'A defect was already created from this finding; link other defects instead.',
    CONTENT_HASH_MISMATCH: 'The content changed meanwhile; reload and try again.',
    STALE_CONTENT: 'The job changed since it was submitted; reload.',
    ALREADY_DECIDED: 'This submission already has a decision.',
    REAUTHENTICATION_REQUIRED: 'Enter your password to confirm.',
    INVALID_CREDENTIALS: 'The password is not correct.',
    DRYDOCK_PROJECT_CLOSED: 'The project is closed; its records are read-only.',
    DRYDOCK_PROJECT_NOT_IN_YARD: 'The project is not in the yard (Dock In first).',
    DRYDOCK_JOB_TRANSITION: 'This step is not possible in the current state of the job.',
    DRYDOCK_JOB_NOT_VERIFIED: 'The job must be verified by the office before it is closed.',
    DRYDOCK_JOB_NOT_APPROVED:
      'The job is not approved by the office (an additional job can start once submitted, by the Master or Chief Engineer).',
    DRYDOCK_RA_REVIEW_REQUIRED:
      'Link an authorised operational RA verification to the job, or record why none is required.',
    DRYDOCK_EVENT_SEQUENCE: 'The job changed meanwhile; reload and try again.',
    INVALID_DRYDOCK_EVENT: 'Check the fields: {{fields}}',
    INVALID_DRYDOCK_VERIFICATION: 'Check the fields: {{fields}}',
    INVALID_PDI_CLOSURE: 'Check the fields: {{fields}}',
    PDI_FINDING_CLOSED: 'The finding is already closed or awaiting office verification.',
    PDI_FINDING_NOT_REVIEWED: 'Review the finding on board first.',
    PDI_FINDING_ACTIONS_OPEN: 'Linked jobs must be Closed or Pending first.',
    PDI_FINDING_DEFECT_OPEN: 'Linked defects must be closed first.',
    PDI_FINDING_NO_ACTION: 'This disposition needs at least one linked job, defect or survey.',
    PDI_FINDING_NOT_CLOSED: 'The finding is not closed.',
    PDI_CLOSURE_NOT_CRITICAL: 'Only a critical finding closure is verified by the office.',
    PDI_FINDING_UNDECIDED: 'Review every finding of the inspection first: {{detail}}',
    PDI_INSPECTION_NOT_CLOSED: 'Close the earlier inspection first.',
    DRYDOCK_LINK_TAKEN: 'This record is already linked to another job.',
    DRYDOCK_JOB_ALREADY_CARRIED: 'This job is already carried to a project.',
    DRYDOCK_JOB_NOT_PENDING: 'Only a Pending job can be carried.',
    INVALID_WORK_ORDER: 'Set the equipment of the job to open a work order.',
    OFFICE_IS_WRITER: 'This record is written by the office.',
    generic: 'The action could not be completed ({{code}}).',
  },
  issues: {
    REQUIRED: 'required',
    INVALID_CODE: 'invalid ID',
    PARENT_MISMATCH: 'parent does not match',
    NOT_ALLOWED: 'not allowed',
    END_BEFORE_START: 'ends before it starts',
    TOO_LONG: 'too long',
    ACCESS_REQUIRES_DEFERRED: 'an inaccessible checkpoint can only be Deferred',
    NOTES_REQUIRED: 'required for an adverse result',
    REASON_REQUIRED: 'reason required',
    FUTURE_DATE: 'cannot be in the future',
    INVALID_DATE: 'invalid date',
    TOO_MANY: 'at most 20',
    LIMITS_REVERSED: 'upper limit is below the lower limit',
    INVALID_NUMBER: 'invalid number',
    INVALID_OPTION: 'invalid option',
    INVALID_ID: 'invalid',
    NOT_CHECKPOINT: 'not a checkpoint',
    IMMEDIATE_ACTION_REQUIRED: 'required for a critical or safety-critical finding',
    INVALID_HASH: 'invalid',
  },
  scope: {
    title: 'Findings and jobs',
    r4: 'Raised by hand: a result never opens a finding, job or defect.',
    noneHere: 'No finding or job at this checkpoint.',
    findings: 'Findings',
    jobs: 'Jobs',
    noFindings: 'No findings yet.',
    noJobs: 'No jobs.',
    noDefects: 'No linked defects.',
    noReviews: 'Not reviewed yet.',
    noSubmissions: 'Not submitted to the office yet.',
    linkedJobs: 'Jobs',
    linkedDefects: 'Defects',
    reviews: 'Technical review',
    submissions: 'Submissions and office decisions',
    origins: 'Origins',
    tbd: 'TBD',
    selfReview: 'self-review',
    ownRecord: 'You raised this record; the company setting does not allow self-review.',
    selfReviewHint:
      'You are reviewing your own record. The company allows it; the record shows it as a self-review.',
    changedSinceReview: 'Changed since review',
    changed: {
      none: '',
      since_submission: 'Changed since submission',
      since_approval: 'Changed since approval — needs approval again',
    },
    defectOpen: 'Open',
    defectClosed: 'Closed',
    alreadyLinked: 'Linked',
    password: 'Password',
    immediateActionHint: 'Required for a critical or safety-critical finding.',
    editReviewedHint: 'Editing a reviewed finding sends it back for review.',
    editSubmittedHint: 'Editing a submitted or approved job sends it back for approval.',
    newFindingTitle: 'New finding',
    editFindingTitle: 'Edit finding {{no}}',
    newJobTitle: 'New job',
    editJobTitle: 'Edit job {{no}}',
    reviewTitle: 'Technical review of {{no}}',
    reviewHint:
      'The Master or Chief Engineer confirms the finding. If it changes later, it needs review again.',
    submitTitle: 'Submit {{no}} to the office',
    submitHint:
      'Technical review on board, then the Superintendent decides. The job and its origins are sent as they are now; a later change needs approval again.',
    cancelFindingTitle: 'Cancel finding {{no}}',
    cancelJobTitle: 'Cancel job {{no}}',
    voidLinkTitle: 'Remove link',
    defectTitle: 'Defect for {{no}}',
    defectHint:
      'Search the open defects first and link one if it already describes the condition. Create a new defect only if none does; not every finding is a defect.',
    defectSearch: 'Search open defects',
    noOpenDefects: 'No open defect found.',
    createDefect: 'Create a new defect',
    addOriginTitle: 'Add origin to {{no}}',
    addOriginHint: 'A job can address several findings or checkpoints.',
    decisionTitle: {
      approved: 'Approve {{no}}',
      returned: 'Return {{no}}',
      rejected: 'Reject {{no}}',
    },
    decisionHint: {
      approved:
        'The job goes to the Yard Job List. If the vessel changes it, it needs approval again.',
      returned: 'The vessel revises the job and submits it again. Write what is to be changed.',
      rejected: 'The job does not go to the yard. Write the reason.',
    },
    tabs: {
      label: 'Inspection view',
      checklist: 'Checklist',
      findings: 'Findings ({{count}})',
      jobs: 'Jobs ({{count}})',
    },
    columns: {
      findingNo: 'Finding No',
      jobNo: 'Job No',
      project: 'Project',
      title: 'Title',
      checkpoint: 'Checkpoint',
      priority: 'Priority',
      disposition: 'Disposition',
      links: 'Jobs / Defects',
      trade: 'Trade',
      quantity: 'Qty',
      planned: 'Planned',
      origins: 'Origins',
      status: 'Status',
    },
    fields: {
      title: 'Title',
      description: 'Description',
      location: 'Location',
      equipment: 'Equipment',
      priority: 'Priority',
      safetyCritical: 'Safety-critical',
      immediateAction: 'Immediate safety action',
      disposition: 'Disposition',
      createdBy: 'Raised by',
      cancelReason: 'Cancel reason',
      scope: 'Scope of work',
      trade: 'Trade',
      contractor: 'Contractor',
      quantity: 'Quantity',
      unit: 'Unit',
      planned: 'Planned',
      plannedStart: 'Planned start',
      plannedEnd: 'Planned end',
      note: 'Note',
      reason: 'Reason',
      severity: 'Severity',
      originType: 'Origin',
      finding: 'Finding',
      checkpointCode: 'Checkpoint ID',
      sourceId: 'Finding',
      targetId: 'Target',
      contentHash: 'Content',
    },
    originTypes: { finding: 'Finding', checkpoint: 'Checkpoint' },
    findingStatus: {
      draft: 'Draft',
      reviewed: 'Reviewed',
      actioned: 'Actioned',
      closing: 'Awaiting office',
      closed: 'Closed',
      cancelled: 'Cancelled',
    },
    jobStatus: {
      draft: 'Draft',
      for_review: 'Awaiting office',
      approved: 'Approved',
      returned: 'Returned',
      rejected: 'Rejected',
      cancelled: 'Cancelled',
    },
    priorities: { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' },
    dispositions: {
      monitor: 'Monitor',
      repair: 'Repair afloat',
      dry_dock: 'Dry dock',
      survey: 'Survey',
      no_action: 'No action',
    },
    trades: {
      hull: 'Hull',
      structural: 'Structural',
      mechanical: 'Mechanical',
      electrical: 'Electrical',
      piping: 'Piping',
      coating: 'Coating',
      outfitting: 'Outfitting',
      safety: 'Safety',
      other: 'Other',
    },
    relations: {
      originates: 'Originates',
      addresses: 'Addresses',
      verified_by: 'Verified by',
      related: 'Related',
    },
    severities: { minor: 'Minor', major: 'Major', critical: 'Critical' },
    actions: {
      newFinding: 'New Finding',
      newJob: 'New Job',
      newJobFromFinding: 'New Job from Finding',
      edit: 'Edit',
      review: 'Review',
      submit: 'Submit to Office',
      cancelFinding: 'Cancel Finding',
      cancelJob: 'Cancel Job',
      defect: 'Link or Create Defect',
      linkDefect: 'Link',
      createDefect: 'Create Defect',
      search: 'Search',
      addOrigin: 'Add Origin',
      voidLink: 'Remove link',
      approve: 'Approve',
      return: 'Return',
      reject: 'Reject',
    },
  },
  jobList: {
    title: 'Yard Job List',
    description:
      'Jobs approved by the office for the yard. A job changed on board needs approval again and leaves the list.',
    officeDescription:
      'Jobs submitted by the vessels and approved for the yard. Approve, return or reject the jobs awaiting the office.',
    error: 'The job list could not be loaded.',
    project: 'Project',
    allProjects: 'All projects',
    filter: 'Jobs',
    filters: {
      yard: 'Yard Job List',
      awaiting: 'Awaiting office',
      verification: 'Awaiting verification',
      pending: 'Pending',
      all: 'All jobs',
    },
  },
  yard: {
    dockIn: 'Dock In',
    undock: 'Undock',
    dockInTitle: 'Dock in {{no}}',
    undockTitle: 'Undock {{no}}',
    dockHint:
      'Record the day the vessel entered the dock. Yard progress can be recorded only while the project is in the yard. The day cannot be changed later.',
    undockHint:
      'Record the day the vessel left the dock. The project stays in the yard until it is closed. The day cannot be changed later.',
    date: 'Date (ship day)',
    on: 'Date',
    dockedOn: 'Docked',
    undockedOn: 'Undocked',
    closedOn: 'Closed',
    closeNote: 'Closing note',
    closeProject: 'Close Project',
    afterDocking: 'Open After Docking Inspection',
    afterDockingHint:
      'Checkpoints deferred in the earlier inspection are inspected again; the frozen library version is kept.',
    closeTitle: 'Close {{no}}',
    closeHint:
      'Jobs completed and verified by the office are closed; jobs not done remain Pending and can be carried to the next project. All in one step; the records are kept.',
    closeBlocked: 'Close the open inspections first: {{list}}',
    closeGroups: {
      close: 'Will be closed',
      pending: 'Will remain Pending',
      alreadyPending: 'Already Pending',
      untouched: 'Not approved, not started (unchanged)',
    },
    awaitingVerification:
      'Completed jobs awaiting office verification will remain Pending: {{list}}',
    openFindings: 'Findings not closed yet (they can still be closed later): {{list}}',
    none: 'None',
    closed:
      'This project is closed: records are read-only. Findings can still be closed and the office can still verify.',
    execution: 'Execution & Verification',
    executionStates: {
      not_started: 'Not started',
      in_progress: 'In progress',
      completed: 'Completed',
      verified: 'Verified',
      closed: 'Closed',
      pending: 'Pending',
    },
    events: {
      started: 'Started',
      progress: 'Progress',
      completed: 'Completed',
      not_done: 'Not done',
      closed: 'Closed',
      pending: 'Pending',
    },
    actions: {
      start: 'Start',
      progress: 'Update Progress',
      complete: 'Complete',
      notDone: 'Not Done',
      close: 'Close Job',
      verify: 'Verify',
      return: 'Return',
      workOrder: 'Open Work Order',
      link: 'Link Record',
      carry: 'Carry to Project',
      closeFinding: 'Close Finding',
      reopenFinding: 'Reopen',
      closeInspection: 'Close Inspection',
      additionalJob: 'Additional Job',
    },
    eventTitle: '{{event}} · {{no}}',
    eventHints: {
      started:
        'Confirm the safety review: link an authorised operational risk verification (and permit to work where the SMS requires one), or record why none is required.',
      progress: 'Percent complete (1–99).',
      completed: 'Record the result, tests and class or maker attendance.',
      not_done: 'Record why the job could not be done in this yard period. It becomes Pending.',
      closed: 'Close the job verified by the office.',
      pending: 'Record why the job remains Pending.',
    },
    safetyReview: 'Safety review',
    safetyReviews: {
      linked: 'Operational RA verification linked',
      not_required: 'Not required (with reason)',
    },
    percent: 'Percent',
    note: 'Note',
    provisional: 'Started before office approval',
    awaitingOffice: 'Awaiting office',
    noEvents: 'No yard progress recorded yet.',
    verifications: 'Office verification',
    verificationTitle: {
      verified: 'Verify {{no}}',
      returned: 'Return {{no}}',
    },
    verificationHint: {
      verified:
        'The completed job is verified with its content hash. It can then be closed on board.',
      returned: 'The job goes back to In progress on board. A reason is required.',
    },
    findingVerificationHint: {
      verified: 'The critical finding closure is verified; the finding becomes Closed.',
      returned: 'The closure is returned; the finding is open again. A reason is required.',
    },
    links: 'Linked records',
    noLinks: 'No linked record.',
    linkTypes: {
      work_order: 'Work order',
      requisition: 'Purchase request',
      ra_verification: 'RA verification',
      survey: 'Survey',
    },
    linkTitle: 'Link a record to {{no}}',
    linkType: 'Record type',
    linkSearch: 'Search',
    noCandidates: 'No record found.',
    carriedFrom: 'Carried from {{job}} ({{project}})',
    carriedTo: 'Carried to {{job}} ({{project}})',
    additional: 'Additional job',
    carryTitle: 'Carry {{no}}',
    carryHint:
      'A new job with the same specification is opened in the selected project. It needs its own technical review and office approval; this record is not changed.',
    carryProject: 'Project',
    closure: 'Closure',
    closeFindingTitle: 'Close {{no}}',
    closeFindingHint:
      'Linked jobs must be Closed or Pending and linked defects closed. A critical finding is verified by the office before it counts as closed.',
    reopenTitle: 'Reopen {{no}}',
    closedBy: 'Closed by {{name}} · {{date}}',
    critical: 'Critical: office verification',
    verdicts: { verified: 'Verified', returned: 'Returned', waiting: 'Awaiting office' },
    surveys: 'Linked surveys',
    queue: 'After Docking queue',
    queueHint: 'Checkpoints deferred in earlier inspections: {{count}}',
    kinds: { pre_docking: 'Pre-Docking', after_docking: 'After Docking' },
    closeInspectionTitle: 'Close {{no}}',
    closeInspectionHint:
      'Every finding of the inspection must be reviewed on board. Results become read-only.',
    columns: { progress: 'Progress', verification: 'Verification' },
  },
  settings: {
    title: 'Dry dock',
    text: 'Pre-docking inspection results, findings and jobs are reviewed on board by the Master or Chief Engineer before they count.',
    selfReview: 'Allow self-review of pre-docking inspection results',
    selfReviewHint:
      'Off: nobody reviews a result, finding or job they recorded. On: they may; the review is marked as a self-review.',
    save: 'Save',
    saved: 'Saved.',
    error: 'The setting could not be saved.',
    retry: 'Try again',
  },
};

export const drydockTr: typeof drydockEn = {
  close: 'Kapat',
  cancel: 'Vazgeç',
  save: 'Kaydet',
  library: {
    title: 'Havuz Öncesi Denetim Kütüphanesi',
    description:
      'Şirketin havuz öncesi denetim (Pre-Docking Inspection) ana kütüphanesi: alanlar, sistemler ve kontrol noktaları; denetim kapsamı, yöntem, zamanlama ve kabul ölçütüyle. Kontrol noktasının kimliği hiç değişmez ve yeniden kullanılmaz. Yayım değiştirilemez bir sürüm açar; denetim açıldığı sürümü kullanır.',
    shipDescription:
      'Şirketin havuz öncesi denetim kütüphanesi, ofisten iner. Gemide salt okunurdur; düzenleme ve yayım ofiste.',
    error: 'Kütüphane yüklenemedi.',
    tree: 'Alanlar ve sistemler',
    checkpoints: 'Kontrol noktaları',
    checkpointList: 'Kontrol noktası listesi',
    allAreas: 'Bütün alanlar',
    versions: 'Yayımlanan sürümler',
    unpublished:
      'Kütüphanede henüz yayımlanmamış değişiklik var. Şimdi açılan denetim son yayımlanmış sürümü kullanır.',
    noVersion: 'Henüz sürüm yayımlanmadı. Denetim ilk yayımdan sonra açılabilir.',
    draft:
      'Felix varsayılan kütüphanesi taslak olarak yüklendi. Alanları, sistemleri ve kontrol noktalarını gözden geçirip ilk sürümü yayımlayın; o zamana dek havuz öncesi denetim açılamaz.',
    draftNoPublish:
      'Felix varsayılan kütüphanesi taslak olarak yüklendi. İlk sürümü Superintendent gözden geçirip yayımlar; o zamana dek havuz öncesi denetim açılamaz.',
    disclaimer:
      'Taslak rehber — klas onaylı kontrol listesi değildir; geminin SMS’i, klas, bayrak ve üretici talimatları önceliklidir.',
  },
  stats: {
    areas: 'Alan',
    systems: 'Sistem',
    checkpoints: 'Kontrol noktası',
    version: 'Yayımlı sürüm',
  },
  columns: {
    code: 'Kimlik',
    checkpoint: 'Kontrol noktası',
    system: 'Sistem',
    method: 'Yöntem',
    timing: 'Zamanlama',
    status: 'Durum',
    version: 'Sürüm',
    label: 'Etiket',
    note: 'Not',
    publishedAt: 'Yayım',
    publishedBy: 'Yayımlayan',
  },
  fields: {
    code: 'Kimlik',
    level: 'Düzey',
    parentCode: 'Üst',
    name: 'Ad',
    sortOrder: 'Sıra',
    active: 'Etkin',
    scope: 'Denetim kapsamı',
    method: 'Yöntem',
    timing: 'Zamanlama',
    acceptance: 'Kabul ölçütü',
    note: 'Yayım notu',
  },
  levels: { area: 'Alan', system: 'Sistem', checkpoint: 'Kontrol noktası' },
  status: { all: 'Bütün durumlar', active: 'Etkin', inactive: 'Pasif' },
  search: 'Kimlik, kontrol noktası ya da kapsamla ara',
  showing: '{{from}}–{{to}} / {{total}}',
  empty: {
    library: 'Kütüphane boş. Felix varsayılan kütüphanesini yükleyin ya da alan ekleyin.',
    filtered: 'Süzgece uyan kontrol noktası yok.',
    versions: 'Yayımlanmış sürüm yok.',
  },
  actions: {
    newArea: 'Yeni Alan',
    newSystem: 'Yeni Sistem',
    newCheckpoint: 'Yeni Kontrol Noktası',
    edit: 'Düzenle',
    publish: 'Sürüm Yayımla',
    loadDefaults: 'Felix Varsayılan Kütüphanesini Yükle',
    reviewPublish: 'Gözden Geçir ve Yayımla',
  },
  node: {
    titleNew: 'Yeni {{level}}',
    titleEdit: '{{code}} düzenle',
    autoCode: 'Kimlik kendiliğinden verilir: {{parent}} altındaki sıradaki boş numara.',
    fixedCode: 'Kimlik, düzey ve üst hiç değişmez. Kaydı silmek yerine pasif yapın.',
  },
  publish: {
    title: 'Kütüphane sürümü yayımla',
    body: 'Etkin kütüphane {{version}}. sürüm olur: yeni denetimlerin kullanacağı değiştirilemez bir kopya. Açık denetimler kendi sürümünde kalır.',
    noteHint: 'İkinci sürümden itibaren zorunlu: ne değişti, neden.',
    unchanged: 'Kütüphane {{version}}. sürümle aynı; yeni sürüm açılmadı.',
    done: '{{version}}. sürüm yayımlandı.',
  },
  defaults: {
    done: '{{version}}: {{areas}} alan, {{systems}} sistem ve {{checkpoints}} kontrol noktası eklendi.',
    published: '{{version}}. sürüm yayımlandı.',
    draft: 'Yayımlanmamış değişiklik olarak eklendi; sürümü Superintendent yayımlar.',
    none: 'Eksik yoktu.',
  },
  detail: {
    title: 'Kontrol noktası',
    path: '{{area}} › {{system}}',
  },
  projects: {
    title: 'Havuzlama Projeleri',
    description:
      'Geminin havuzlama ve yüzerken onarım projeleri. Havuz öncesi denetim şirketin yayımlı kütüphanesinden açılır; sürümü denetim için dondurulur.',
    officeDescription: 'Filonun havuzlama projeleri, gemide kaydedilir. Ofiste salt okunur.',
    error: 'Projeler yüklenemedi.',
    list: 'Proje listesi',
    empty: 'Henüz havuzlama projesi yok.',
    new: 'Yeni Proje',
    titleNew: 'Yeni havuzlama projesi',
    titleEdit: '{{no}} düzenle',
    cancelTitle: '{{no}} iptal',
    cancelBody: 'Proje ve açık denetimi gerekçeyle iptal edilir. Geri alınamaz; kayıt korunur.',
    reason: 'Gerekçe',
    cancelProject: 'Projeyi İptal Et',
    openInspection: 'Havuz Öncesi Denetim Aç',
    inspections: 'Havuz öncesi denetimler',
    noInspection: 'Açılmış denetim yok.',
    libraryVersion: 'Kütüphane v{{version}} · {{count}} kontrol noktası',
    libraryNow: 'Şirket kütüphanesi şu an v{{version}}.',
    vessel: 'Gemi',
    allVessels: 'Bütün gemiler',
    noLibrary:
      'Henüz yayımlanmış denetim kütüphanesi sürümü yok; bu yüzden havuz öncesi denetim açılamaz. Kütüphaneyi Superintendent Havuzlama ▸ Denetim Kütüphanesi ekranında gözden geçirip yayımlar.',
    goInspection: 'Havuz Öncesi Denetimde Aç',
  },
  projectColumns: {
    no: 'Proje No',
    vessel: 'Gemi',
    title: 'Başlık',
    kind: 'Tür',
    yard: 'Tersane',
    planned: 'Plan',
    state: 'Durum',
    inspection: 'Denetim',
  },
  projectFields: {
    title: 'Proje başlığı',
    kind: 'Tür',
    yardName: 'Tersane',
    yardLocation: 'Yer',
    plannedStart: 'Planlanan başlangıç',
    plannedEnd: 'Planlanan bitiş',
    notes: 'Notlar',
  },
  kinds: { dry_docking: 'Havuzlama', afloat_repair: 'Yüzerken onarım' },
  states: {
    planning: 'Planlama',
    in_yard: 'Tersanede',
    completed: 'Tamamlandı',
    cancelled: 'İptal',
  },
  inspectionStates: { open: 'Açık', closed: 'Kapalı', cancelled: 'İptal' },
  inspection: {
    title: '{{no}} denetimi',
    frozen:
      '{{date}} tarihinde kütüphane {{version}}. sürümüyle açıldı. Sonuçlar Havuzlama ▸ Havuz Öncesi Denetim ekranında girilir.',
  },
  work: {
    title: 'Havuz Öncesi Denetim',
    description:
      'Havuz öncesi denetimi kontrol noktası kontrol noktası kaydedin: sonuç, erişim, denetçi, ölçümler ve kanıt. Yalnız kontrol noktaları denetlenir. Kaydedilen sonuç değişmez; düzeltme gerekçeli yeni revizyondur. Sonuçları Kaptan ya da Başmühendis inceler. Sonuç kaydı bulgu, iş ya da arıza açmaz.',
    officeDescription: 'Filonun havuz öncesi denetimleri, gemide kaydedilir. Ofiste salt okunur.',
    error: 'Denetim yüklenemedi.',
    vessel: 'Gemi',
    allVessels: 'Bütün gemiler',
    inspection: 'Denetim',
    noInspection: 'Denetim yok',
    empty: 'Henüz havuz öncesi denetim yok. Bir havuzlama projesinden açın.',
    noLibrary:
      'Henüz yayımlanmış denetim kütüphanesi sürümü yok; bu yüzden denetim açılamaz. Kütüphaneyi Superintendent Havuzlama ▸ Denetim Kütüphanesi ekranında gözden geçirip yayımlar.',
    closed: 'Bu denetim açık değil: sonuçlar salt okunur.',
    progress: 'Denetim ilerlemesi %{{percent}}',
    progressText:
      '{{applicable}} uygulanabilir kontrol noktasından {{done}} tanesi denetlendi (%{{percent}}).',
    filter: 'Göster',
    filters: {
      all: 'Tümü',
      remaining: 'Kalan',
      adverse: 'Olumsuz',
      after_docking: 'Havuzdan sonra',
    },
    stats: {
      applicable: 'Uygulanabilir',
      done: 'Denetlenen',
      remaining: 'Kalan',
      adverse: 'Olumsuz',
      deferred: 'Havuzdan sonra',
      notApplicable: 'Uygulanamaz',
    },
    columns: {
      code: 'Kimlik',
      checkpoint: 'Kontrol noktası',
      system: 'Sistem',
      status: 'Durum',
      inspected: 'Denetim (LT)',
      inspector: 'Denetçi',
      evidence: 'Kanıt',
      pages: 'Kontrol noktası sayfaları',
    },
    status: {
      satisfactory: 'Uygun',
      attention_required: 'Dikkat gerekli',
      unsatisfactory: 'Uygun değil',
      not_applicable: 'Uygulanamaz',
      deferred: 'Ertelendi',
      not_inspected: 'Denetlenmedi',
      vessel_na: 'Gemide yok',
    },
    access: {
      accessible: 'Erişilebilir',
      inaccessible: 'Erişilemez',
      after_docking: 'Havuzdan sonra',
    },
    applicability: {
      applicable: 'Uygulanabilir',
      not_applicable: 'Gemiye uygulanamaz',
      review: 'Uygunluk incelenecek',
    },
    verdict: { pass: 'Uygun', fail: 'Uygun değil', none: 'Sınır yok' },
    result: 'Sonuç',
    noResult: 'Henüz denetlenmedi.',
    vesselNa: 'Bu gemiye uygulanamaz: sayıma girmez ve sonuç almaz.',
    history: 'Revizyon geçmişi',
    revision: 'Rev. {{no}}',
    revisionBy: '{{no}}. revizyon · kaydeden {{name}} · {{date}}',
    reviewed: 'İnceleyen {{name}} · {{date}}',
    notReviewed: 'Henüz incelenmedi.',
    recordTitle: 'Sonuç kaydet · {{code}}',
    correctTitle: 'Sonucu düzelt · {{code}}',
    correctHint:
      'Kaydedilen sonuç değişmez: düzeltme gerekçenizle yeni revizyon olarak saklanır, kanıt son revizyona eklenir.',
    applicabilityTitle: 'Gemiye uygunluk · {{code}}',
    applicabilityHint:
      'Gemi profili bu geminin bütün denetimlerinde geçerlidir. Gemiye uygulanamayan kontrol noktası sayıma girmez ve sonuç almaz.',
    reviewTitle: 'Sonucu incele · {{code}}',
    reviewHint:
      'Sonucu Kaptan ya da Başmühendis inceler; kimse kendi denetlediği ya da kaydettiği sonucu incelemez.',
    actions: {
      record: 'Sonuç Kaydet',
      correct: 'Sonucu Düzelt',
      review: 'İncele',
      applicability: 'Uygunluğu Belirle',
    },
  },
  resultFields: {
    result: 'Sonuç',
    access: 'Erişim',
    inspectedAt: 'Denetim zamanı',
    inspector: 'Denetçi',
    inspectorId: 'Denetçi',
    equipment: 'Ekipman',
    equipmentId: 'Ekipman',
    noEquipment: 'Ekipman yok',
    notes: 'Durum notu',
    notesRequired: 'Dikkat gerekli ve Uygun değil için zorunlu.',
    reasonRequired: 'Gerekçeyi yazın (Uygulanamaz ve Ertelendi için zorunlu).',
    measurements: 'Ölçümler',
    measurementNo: '{{no}}. ölçüm',
    noMeasurements: 'Ölçüm yok.',
    addMeasurement: 'Ölçüm Ekle',
    removeMeasurement: 'Ölçümü kaldır',
    label: 'Ölçüm',
    value: 'Değer',
    unit: 'Birim',
    lower: 'Alt sınır',
    upper: 'Üst sınır',
    limits: 'Sınırlar',
    verdict: 'Karar',
    reference: 'Kabul kaynağı',
    correctionReason: 'Düzeltme gerekçesi',
    applicability: 'Uygunluk',
    applicabilityReason: 'Gerekçe',
    reason: 'Gerekçe',
    reviewNote: 'İnceleme notu',
    evidence: 'Kanıt',
    noEvidence: 'Kanıt eklenmedi.',
    addEvidence: 'Kanıt Ekle',
    checkpointCode: 'Kontrol noktası',
  },
  errors: {
    INVALID_PDI_NODE: 'Alanları denetleyin: {{fields}}',
    INVALID_DRYDOCK_PROJECT: 'Alanları denetleyin: {{fields}}',
    DUPLICATE_PDI_CODE: 'Bu kimlik zaten var (kimlik yeniden kullanılmaz).',
    PDI_CODE_FIXED: 'Kaydın kimliği, düzeyi ve üstü değişemez.',
    PDI_PARENT_MISSING: 'Üst kayıt yok.',
    PDI_PARENT_INACTIVE: 'Üst kayıt pasif.',
    PDI_CODE_EXHAUSTED: 'Bu üst altında boş kimlik kalmadı.',
    PDI_LIBRARY_EMPTY: 'Kütüphanede yayımlanacak etkin kontrol noktası yok.',
    PDI_NOTE_REQUIRED: 'Yayım notu yazın.',
    PDI_SNAPSHOT_MISMATCH: 'Kütüphane bu arada değişti; yenileyip yeniden yayımlayın.',
    PDI_LIBRARY_NOT_PUBLISHED:
      'Henüz yayımlanmış kütüphane sürümü yok. Önce Superintendent kütüphaneyi gözden geçirip yayımlar.',
    INVALID_PDI_RESULT: 'Alanları denetleyin: {{fields}}',
    INVALID_PDI_APPLICABILITY: 'Alanları denetleyin: {{fields}}',
    PDI_NOT_CHECKPOINT: 'Yalnız kontrol noktası denetlenir (alan ya da sistem değil).',
    PDI_CHECKPOINT_UNKNOWN: 'Bu kontrol noktası kütüphanede yok.',
    PDI_REVISION_SEQUENCE: 'Sonuç bu arada değişti; yenileyip son revizyonu düzeltin.',
    PDI_RESULT_SUPERSEDED: 'Daha yeni bir revizyon var; son revizyonla çalışın.',
    PDI_ALREADY_REVIEWED: 'Bu sonuç zaten incelendi.',
    PDI_NOT_APPLICABLE: 'Kontrol noktası gemiye uygulanamaz; sonuç almaz.',
    PDI_INSPECTION_CLOSED: 'Denetim açık değil.',
    PDI_INSPECTOR_NOT_ON_BOARD: 'Denetçi o gün gemide değildi.',
    SELF_APPROVAL_FORBIDDEN:
      'Kendi denetlediğiniz ya da açtığınız kaydı inceleyemezsiniz; şirket ayarı kendi incelemesine izin vermiyor.',
    VESSEL_MISMATCH: 'Ekipman başka bir gemiye ait.',
    NOT_FOUND: 'Kayıt bulunamadı.',
    PDI_INSPECTION_OPEN: 'Bu projede açık bir denetim var.',
    DRYDOCK_PROJECT_CANCELLED: 'Proje iptal edilmiş.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    ROW_VERSION_CONFLICT: 'Kayıt bu arada değişti; yenileyip yeniden deneyin.',
    INVALID_PDI_FINDING: 'Alanları denetleyin: {{fields}}',
    INVALID_DRYDOCK_JOB: 'Alanları denetleyin: {{fields}}',
    INVALID_DRYDOCK_LINK: 'Bağı denetleyin: {{fields}}',
    INVALID_DRYDOCK_DECISION: 'Alanları denetleyin: {{fields}}',
    DRYDOCK_RECORD_FIXED: 'Kaydın numarası, projesi, denetimi ve kontrol noktası değişmez.',
    DRYDOCK_RECORD_CANCELLED: 'Kayıt iptal edilmiş.',
    DUPLICATE_LINK: 'Bu bağ zaten var.',
    DRYDOCK_REVIEW_SEQUENCE: 'Kayıt bu arada değişti; yeniden yükleyip deneyin.',
    DRYDOCK_ALREADY_REVIEWED: 'Bu içerik zaten incelenmiş ya da ofiste bekliyor.',
    DRYDOCK_JOB_UNCHANGED: 'Yeniden göndermeden önce işi değiştirin.',
    DRYDOCK_JOB_NO_ORIGIN: 'İşin kaynağı bir bulgu ya da kontrol noktası olmalı.',
    DRYDOCK_DEFECT_EXISTS: 'Bu bulgudan zaten arıza açıldı; başka arızaları bağlayın.',
    CONTENT_HASH_MISMATCH: 'İçerik bu arada değişti; yeniden yükleyip deneyin.',
    STALE_CONTENT: 'İş gönderildikten sonra değişti; yeniden yükleyin.',
    ALREADY_DECIDED: 'Bu gönderime zaten karar verilmiş.',
    REAUTHENTICATION_REQUIRED: 'Onaylamak için parolanızı girin.',
    INVALID_CREDENTIALS: 'Parola doğru değil.',
    DRYDOCK_PROJECT_CLOSED: 'Proje kapalı; kayıtları salt okunur.',
    DRYDOCK_PROJECT_NOT_IN_YARD: 'Proje tersanede değil (önce Havuza Giriş).',
    DRYDOCK_JOB_TRANSITION: 'İşin bugünkü durumunda bu adım yapılamaz.',
    DRYDOCK_JOB_NOT_VERIFIED: 'İş kapanmadan önce ofisçe doğrulanmalı.',
    DRYDOCK_JOB_NOT_APPROVED:
      'İş ofisçe onaylanmadı (ek iş gönderildikten sonra Kaptan ya da Başmühendis tarafından başlatılabilir).',
    DRYDOCK_RA_REVIEW_REQUIRED:
      'İşe yetkilendirilmiş bir operasyonel RA doğrulaması bağlayın ya da neden gerekmediğini yazın.',
    DRYDOCK_EVENT_SEQUENCE: 'İş bu arada değişti; yenileyip tekrar deneyin.',
    INVALID_DRYDOCK_EVENT: 'Alanları kontrol edin: {{fields}}',
    INVALID_DRYDOCK_VERIFICATION: 'Alanları kontrol edin: {{fields}}',
    INVALID_PDI_CLOSURE: 'Alanları kontrol edin: {{fields}}',
    PDI_FINDING_CLOSED: 'Bulgu zaten kapalı ya da ofis doğrulamasını bekliyor.',
    PDI_FINDING_NOT_REVIEWED: 'Önce bulguyu gemide inceleyin.',
    PDI_FINDING_ACTIONS_OPEN: 'Önce bağlı işler Kapandı ya da Pending olmalı.',
    PDI_FINDING_DEFECT_OPEN: 'Önce bağlı arızalar kapanmalı.',
    PDI_FINDING_NO_ACTION: 'Bu karar en az bir bağlı iş, arıza ya da sörvey ister.',
    PDI_FINDING_NOT_CLOSED: 'Bulgu kapalı değil.',
    PDI_CLOSURE_NOT_CRITICAL: 'Ofis yalnız kritik bulgu kapanışını doğrular.',
    PDI_FINDING_UNDECIDED: 'Önce denetimin bütün bulgularını inceleyin: {{detail}}',
    PDI_INSPECTION_NOT_CLOSED: 'Önce önceki denetimi kapatın.',
    DRYDOCK_LINK_TAKEN: 'Bu kayıt başka bir işe bağlı.',
    DRYDOCK_JOB_ALREADY_CARRIED: 'Bu iş zaten bir projeye taşındı.',
    DRYDOCK_JOB_NOT_PENDING: 'Yalnız Pending iş taşınabilir.',
    INVALID_WORK_ORDER: 'İş emri açmak için işin ekipmanını seçin.',
    OFFICE_IS_WRITER: 'Bu kaydı ofis yazar.',
    generic: 'İşlem tamamlanamadı ({{code}}).',
  },
  issues: {
    REQUIRED: 'zorunlu',
    INVALID_CODE: 'geçersiz kimlik',
    PARENT_MISMATCH: 'üst uyuşmuyor',
    NOT_ALLOWED: 'izin verilmez',
    END_BEFORE_START: 'başlangıçtan önce bitiyor',
    TOO_LONG: 'çok uzun',
    ACCESS_REQUIRES_DEFERRED: 'erişilemeyen nokta yalnız Ertelendi olabilir',
    NOTES_REQUIRED: 'olumsuz sonuçta zorunlu',
    REASON_REQUIRED: 'gerekçe zorunlu',
    FUTURE_DATE: 'gelecekte olamaz',
    INVALID_DATE: 'geçersiz tarih',
    TOO_MANY: 'en çok 20',
    LIMITS_REVERSED: 'üst sınır alt sınırdan küçük',
    INVALID_NUMBER: 'geçersiz sayı',
    INVALID_OPTION: 'geçersiz seçenek',
    INVALID_ID: 'geçersiz',
    NOT_CHECKPOINT: 'kontrol noktası değil',
    IMMEDIATE_ACTION_REQUIRED: 'kritik ya da güvenlik açısından kritik bulguda zorunlu',
    INVALID_HASH: 'geçersiz',
  },
  scope: {
    title: 'Bulgular ve işler',
    r4: 'Elle açılır: sonuç kaydı bulgu, iş ya da arıza açmaz.',
    noneHere: 'Bu noktada bulgu ya da iş yok.',
    findings: 'Bulgular',
    jobs: 'İşler',
    noFindings: 'Henüz bulgu yok.',
    noJobs: 'İş yok.',
    noDefects: 'Bağlı arıza yok.',
    noReviews: 'Henüz incelenmedi.',
    noSubmissions: 'Henüz ofise gönderilmedi.',
    linkedJobs: 'İşler',
    linkedDefects: 'Arızalar',
    reviews: 'Teknik inceleme',
    submissions: 'Gönderimler ve ofis kararları',
    origins: 'Kaynaklar',
    tbd: 'Belirlenecek',
    selfReview: 'kendi incelemesi',
    ownRecord: 'Bu kaydı siz açtınız; şirket ayarı kendi incelemesine izin vermiyor.',
    selfReviewHint:
      'Kendi kaydınızı inceliyorsunuz. Şirket buna izin veriyor; kayıt kendi incelemesi olarak görünür.',
    changedSinceReview: 'İncelemeden sonra değişti',
    changed: {
      none: '',
      since_submission: 'Gönderimden sonra değişti',
      since_approval: 'Onaydan sonra değişti — yeniden onay gerekir',
    },
    defectOpen: 'Açık',
    defectClosed: 'Kapalı',
    alreadyLinked: 'Bağlı',
    password: 'Parola',
    immediateActionHint: 'Kritik ya da güvenlik açısından kritik bulguda zorunlu.',
    editReviewedHint: 'İncelenmiş bulguyu değiştirmek onu yeniden incelemeye düşürür.',
    editSubmittedHint: 'Gönderilmiş ya da onaylı işi değiştirmek onu yeniden onaya düşürür.',
    newFindingTitle: 'Yeni bulgu',
    editFindingTitle: '{{no}} bulgusunu düzelt',
    newJobTitle: 'Yeni iş',
    editJobTitle: '{{no}} işini düzelt',
    reviewTitle: '{{no}} teknik incelemesi',
    reviewHint:
      'Kaptan ya da Başmühendis bulguyu doğrular. Sonra değişirse yeniden inceleme gerekir.',
    submitTitle: '{{no}} işini ofise gönder',
    submitHint:
      'Gemide teknik inceleme, ardından Superintendent karar verir. İş ve kaynakları şimdiki haliyle gönderilir; sonraki değişiklik yeniden onay ister.',
    cancelFindingTitle: '{{no}} bulgusunu iptal et',
    cancelJobTitle: '{{no}} işini iptal et',
    voidLinkTitle: 'Bağı kaldır',
    defectTitle: '{{no}} için arıza',
    defectHint:
      'Önce açık arızaları arayın; durumu zaten anlatan arıza varsa onu bağlayın. Yoksa yeni arıza açın; her bulgu arıza değildir.',
    defectSearch: 'Açık arızalarda ara',
    noOpenDefects: 'Açık arıza bulunamadı.',
    createDefect: 'Yeni arıza aç',
    addOriginTitle: '{{no}} işine kaynak ekle',
    addOriginHint: 'Bir iş birden çok bulguyu ya da noktayı karşılayabilir.',
    decisionTitle: {
      approved: '{{no}} işini onayla',
      returned: '{{no}} işini iade et',
      rejected: '{{no}} işini reddet',
    },
    decisionHint: {
      approved: 'İş tersane iş listesine düşer. Gemi değiştirirse yeniden onay gerekir.',
      returned: 'Gemi işi düzeltip yeniden gönderir. Neyin değişeceğini yazın.',
      rejected: 'İş tersaneye gitmez. Gerekçeyi yazın.',
    },
    tabs: {
      label: 'Denetim görünümü',
      checklist: 'Kontrol listesi',
      findings: 'Bulgular ({{count}})',
      jobs: 'İşler ({{count}})',
    },
    columns: {
      findingNo: 'Bulgu No',
      jobNo: 'İş No',
      project: 'Proje',
      title: 'Başlık',
      checkpoint: 'Kontrol noktası',
      priority: 'Öncelik',
      disposition: 'Karar',
      links: 'İşler / Arızalar',
      trade: 'İş kolu',
      quantity: 'Miktar',
      planned: 'Plan',
      origins: 'Kaynaklar',
      status: 'Durum',
    },
    fields: {
      title: 'Başlık',
      description: 'Açıklama',
      location: 'Yer',
      equipment: 'Ekipman',
      priority: 'Öncelik',
      safetyCritical: 'Güvenlik açısından kritik',
      immediateAction: 'Acil güvenlik önlemi',
      disposition: 'Karar',
      createdBy: 'Açan',
      cancelReason: 'İptal gerekçesi',
      scope: 'İşin kapsamı',
      trade: 'İş kolu',
      contractor: 'Yüklenici',
      quantity: 'Miktar',
      unit: 'Birim',
      planned: 'Plan',
      plannedStart: 'Plan başlangıcı',
      plannedEnd: 'Plan bitişi',
      note: 'Not',
      reason: 'Gerekçe',
      severity: 'Önem',
      originType: 'Kaynak',
      finding: 'Bulgu',
      checkpointCode: 'Kontrol noktası kimliği',
      sourceId: 'Bulgu',
      targetId: 'Hedef',
      contentHash: 'İçerik',
    },
    originTypes: { finding: 'Bulgu', checkpoint: 'Kontrol noktası' },
    findingStatus: {
      draft: 'Taslak',
      reviewed: 'İncelendi',
      actioned: 'İşleme alındı',
      closing: 'Ofiste bekliyor',
      closed: 'Kapandı',
      cancelled: 'İptal',
    },
    jobStatus: {
      draft: 'Taslak',
      for_review: 'Ofiste bekliyor',
      approved: 'Onaylandı',
      returned: 'İade edildi',
      rejected: 'Reddedildi',
      cancelled: 'İptal',
    },
    priorities: { low: 'Düşük', medium: 'Orta', high: 'Yüksek', critical: 'Kritik' },
    dispositions: {
      monitor: 'İzle',
      repair: 'Seyirde onar',
      dry_dock: 'Havuzda',
      survey: 'Sörvey',
      no_action: 'İşlem yok',
    },
    trades: {
      hull: 'Tekne',
      structural: 'Yapısal',
      mechanical: 'Mekanik',
      electrical: 'Elektrik',
      piping: 'Boru',
      coating: 'Boya',
      outfitting: 'Donatım',
      safety: 'Emniyet',
      other: 'Diğer',
    },
    relations: {
      originates: 'Doğurur',
      addresses: 'Karşılar',
      verified_by: 'Doğrulayan',
      related: 'İlgili',
    },
    severities: { minor: 'Küçük', major: 'Büyük', critical: 'Kritik' },
    actions: {
      newFinding: 'Yeni Bulgu',
      newJob: 'Yeni İş',
      newJobFromFinding: 'Bulgudan Yeni İş',
      edit: 'Düzelt',
      review: 'İncele',
      submit: 'Ofise Gönder',
      cancelFinding: 'Bulguyu İptal Et',
      cancelJob: 'İşi İptal Et',
      defect: 'Arıza Bağla ya da Aç',
      linkDefect: 'Bağla',
      createDefect: 'Arıza Aç',
      search: 'Ara',
      addOrigin: 'Kaynak Ekle',
      voidLink: 'Bağı kaldır',
      approve: 'Onayla',
      return: 'İade Et',
      reject: 'Reddet',
    },
  },
  jobList: {
    title: 'Tersane İş Listesi',
    description:
      'Ofisin tersane için onayladığı işler. Gemide değişen iş yeniden onaya düşer ve listeden çıkar.',
    officeDescription:
      'Gemilerin gönderdiği ve tersane için onaylanan işler. Ofiste bekleyen işleri onaylayın, iade edin ya da reddedin.',
    error: 'İş listesi yüklenemedi.',
    project: 'Proje',
    allProjects: 'Bütün projeler',
    filter: 'İşler',
    filters: {
      yard: 'Tersane İş Listesi',
      awaiting: 'Ofiste bekleyen',
      verification: 'Doğrulama bekleyen',
      pending: 'Pending',
      all: 'Bütün işler',
    },
  },
  yard: {
    dockIn: 'Havuza Giriş',
    undock: 'Havuzdan Çıkış',
    dockInTitle: '{{no}} havuza giriş',
    undockTitle: '{{no}} havuzdan çıkış',
    dockHint:
      'Geminin havuza girdiği günü kaydedin. Tersane ilerlemesi yalnız proje tersanedeyken girilir. Gün sonra değiştirilemez.',
    undockHint:
      'Geminin havuzdan çıktığı günü kaydedin. Proje kapanana kadar tersanede kalır. Gün sonra değiştirilemez.',
    date: 'Tarih (gemi günü)',
    on: 'Tarih',
    dockedOn: 'Havuza giriş',
    undockedOn: 'Havuzdan çıkış',
    closedOn: 'Kapanış',
    closeNote: 'Kapanış notu',
    closeProject: 'Projeyi Kapat',
    afterDocking: 'Havuz Sonrası Denetimi Aç',
    afterDockingHint:
      'Önceki denetimde ertelenen noktalar yeniden denetlenir; dondurulmuş kütüphane sürümü korunur.',
    closeTitle: '{{no}} kapanışı',
    closeHint:
      'Tamamlanıp ofisçe doğrulanan işler kapanır; yapılmayan işler Pending kalır ve sonraki projeye taşınabilir. Hepsi tek adımda; kayıtlar korunur.',
    closeBlocked: 'Önce açık denetimleri kapatın: {{list}}',
    closeGroups: {
      close: 'Kapanacak',
      pending: 'Pending kalacak',
      alreadyPending: 'Zaten Pending',
      untouched: 'Onaylanmamış, başlamamış (değişmez)',
    },
    awaitingVerification: 'Ofis doğrulamasını bekleyen tamamlanmış işler Pending kalır: {{list}}',
    openFindings: 'Henüz kapanmamış bulgular (sonra da kapatılabilir): {{list}}',
    none: 'Yok',
    closed:
      'Bu proje kapalı: kayıtlar salt okunur. Bulgular yine kapatılabilir, ofis yine doğrulayabilir.',
    execution: 'İcra ve Doğrulama',
    executionStates: {
      not_started: 'Başlamadı',
      in_progress: 'Sürüyor',
      completed: 'Tamamlandı',
      verified: 'Doğrulandı',
      closed: 'Kapandı',
      pending: 'Pending',
    },
    events: {
      started: 'Başladı',
      progress: 'İlerleme',
      completed: 'Tamamlandı',
      not_done: 'Yapılamadı',
      closed: 'Kapandı',
      pending: 'Pending',
    },
    actions: {
      start: 'Başlat',
      progress: 'İlerleme Gir',
      complete: 'Tamamla',
      notDone: 'Yapılamadı',
      close: 'İşi Kapat',
      verify: 'Doğrula',
      return: 'İade Et',
      workOrder: 'İş Emri Aç',
      link: 'Kayıt Bağla',
      carry: 'Projeye Taşı',
      closeFinding: 'Bulguyu Kapat',
      reopenFinding: 'Yeniden Aç',
      closeInspection: 'Denetimi Kapat',
      additionalJob: 'Ek İş',
    },
    eventTitle: '{{event}} · {{no}}',
    eventHints: {
      started:
        'Güvenlik incelemesini onaylayın: yetkilendirilmiş operasyonel risk doğrulamasını (SMS gerektiriyorsa çalışma izniyle) bağlayın ya da neden gerekmediğini yazın.',
      progress: 'Tamamlanma yüzdesi (1–99).',
      completed: 'Sonucu, testleri ve klas ya da üretici katılımını yazın.',
      not_done: 'İşin bu tersane döneminde neden yapılamadığını yazın. İş Pending olur.',
      closed: 'Ofisin doğruladığı işi kapatın.',
      pending: 'İşin neden Pending kaldığını yazın.',
    },
    safetyReview: 'Güvenlik incelemesi',
    safetyReviews: {
      linked: 'Operasyonel RA doğrulaması bağlı',
      not_required: 'Gerekmez (gerekçeli)',
    },
    percent: 'Yüzde',
    note: 'Not',
    provisional: 'Ofis onayından önce başlatıldı',
    awaitingOffice: 'Ofiste bekliyor',
    noEvents: 'Henüz tersane ilerlemesi girilmedi.',
    verifications: 'Ofis doğrulaması',
    verificationTitle: {
      verified: '{{no}} doğrula',
      returned: '{{no}} iade et',
    },
    verificationHint: {
      verified: 'Tamamlanan iş içerik hash’iyle doğrulanır. Sonra gemide kapatılabilir.',
      returned: 'İş gemide yeniden Sürüyor durumuna döner. Gerekçe zorunlu.',
    },
    findingVerificationHint: {
      verified: 'Kritik bulgunun kapanışı doğrulanır; bulgu Kapandı olur.',
      returned: 'Kapanış iade edilir; bulgu yeniden açık olur. Gerekçe zorunlu.',
    },
    links: 'Bağlı kayıtlar',
    noLinks: 'Bağlı kayıt yok.',
    linkTypes: {
      work_order: 'İş emri',
      requisition: 'Satın alma talebi',
      ra_verification: 'RA doğrulaması',
      survey: 'Sörvey',
    },
    linkTitle: '{{no}} için kayıt bağla',
    linkType: 'Kayıt türü',
    linkSearch: 'Ara',
    noCandidates: 'Kayıt bulunamadı.',
    carriedFrom: '{{job}} işinden taşındı ({{project}})',
    carriedTo: '{{job}} işine taşındı ({{project}})',
    additional: 'Ek iş',
    carryTitle: '{{no}} taşı',
    carryHint:
      'Seçilen projede aynı spesifikasyonla yeni bir iş açılır. Kendi teknik incelemesine ve ofis onayına gider; bu kayıt değişmez.',
    carryProject: 'Proje',
    closure: 'Kapanış',
    closeFindingTitle: '{{no}} kapat',
    closeFindingHint:
      'Bağlı işler Kapandı ya da Pending, bağlı arızalar kapalı olmalı. Kritik bulgu kapanmış sayılmadan önce ofisçe doğrulanır.',
    reopenTitle: '{{no}} yeniden aç',
    closedBy: '{{name}} kapattı · {{date}}',
    critical: 'Kritik: ofis doğrulaması',
    verdicts: { verified: 'Doğrulandı', returned: 'İade edildi', waiting: 'Ofiste bekliyor' },
    surveys: 'Bağlı sörveyler',
    queue: 'Havuz sonrası kuyruğu',
    queueHint: 'Önceki denetimlerde ertelenen noktalar: {{count}}',
    kinds: { pre_docking: 'Havuz Öncesi', after_docking: 'Havuz Sonrası' },
    closeInspectionTitle: '{{no}} kapat',
    closeInspectionHint:
      'Denetimin bütün bulguları gemide incelenmiş olmalı. Sonuçlar salt okunur olur.',
    columns: { progress: 'İlerleme', verification: 'Doğrulama' },
  },
  settings: {
    title: 'Havuzlama',
    text: 'Havuz öncesi denetim sonuçları, bulgular ve işler sayılmadan önce gemide Kaptan ya da Başmühendis tarafından incelenir.',
    selfReview: 'Havuz öncesi denetim sonuçlarında kendi incelemesine izin ver',
    selfReviewHint:
      'Kapalı: kimse kendi kaydettiği sonucu, bulguyu ya da işi inceleyemez. Açık: inceleyebilir; inceleme kendi incelemesi olarak işaretlenir.',
    save: 'Kaydet',
    saved: 'Kaydedildi.',
    error: 'Ayar kaydedilemedi.',
    retry: 'Yeniden dene',
  },
};
